import React, { useEffect, useState } from "react";
import { Button, Col, Image, Row, Stack } from "react-bootstrap";
import { MdOutlineInfo } from "react-icons/md";
import { useSelector } from "react-redux";
import { Link, createSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LOGO_WHITE from "../../assets/images/logo.svg";
import REGISTER_BANNER from "../../assets/images/register-banner.svg";
import CommonFormikComponent from "../../components/common/CommonFormikComponent";
import CustomInputPassword from "../../components/common/CustomInputPassword";
import { default as CustomInputText } from "../../components/common/CustomInputText";
import Loader from "../../components/common/loader";
import { PASSWORD_VALIDATION } from "../../constants/messages";
import sessionStorage from "../../helpers/sessionStorage";
import storage from "../../helpers/storage";
import { registerUserAPI } from "../../services/auth.service";
import { CreateAccountSchema } from "./validation";
/**
 * Form values type definition.
 */
type FormValues = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export default function CreateAccount() {
  const AppName = process.env.REACT_APP_NAME;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  /**
   * Initial Form Values
   */
  const initialValues: FormValues = {
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  };

  useEffect(() => {
    if (storage.getToken() != null) {
      return navigate("/dashboard", { replace: true });
    } else {
      localStorage.removeItem("user-info");
      storage.clearToken();
    }
  }, [navigate]);

  /**
   * Submit Method to call when user clicks on Create Account button
   * @async
   * @param {FormValues} values
   * @param {any} actions
   * @returns {*}
   */
  const handleSubmit = async (values: Record<string, any>, actions: any) => {
    setLoading(true);
    actions.setSubmitting(true);
    const formData: any = {
      fullName: values.fullName,
      email: values.email,
      password: values.password,
    };
    registerUserAPI(formData)
      .then((response: any) => {
        actions.setSubmitting(false);
        setLoading(false);
        handleUserRegisterResponse(response);
      })
      .catch((error: any) => {
        console.log(error);
        setLoading(false);
        toast(error.data.message, { type: toast.TYPE.ERROR });
        actions.setSubmitting(false);
      });
  };

  /**
   * Handles the response after a successful registration.
   * @param {Object} responseData - The response data from the registration API.
   * @returns {void}
   */
  const handleUserRegisterResponse = (responseData: any) => {
    console.log(responseData);
    toast(responseData.message, { type: toast.TYPE.SUCCESS });
    setLoading(false);
    storage.setToken(responseData.data.token.access.token);
    storage.setRefreshToken(responseData.data.token.refresh.token);
    navigate("/my-profile", { replace: true });
  };

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <Row className="g-0 vh-100">
        <Col md={6}>
          <Stack
            className="custom-primary-gradient text-center justify-content-around h-100 p-4"
            gap={4}
          >
            <div className="user-select-none">
              <Image
                fluid={true}
                src={LOGO_WHITE}
                alt={`${AppName} Logo`}
                width={158}
                height={48}
              />
            </div>
            <div>
              <Image
                fluid={true}
                src={REGISTER_BANNER}
                alt={`${AppName} Customer Panel`}
                width={400}
                height={392}
              />
            </div>
            <div className="lh-sm text-white">
              <div className="fw-light fs-4">Advanced Dynamic</div>
              <div className="fw-semibold fs-1 text-uppercase">
                Vulnerability Scanning
              </div>
              <div className="fw-light fs-4">for Enterprises</div>
            </div>
          </Stack>
        </Col>
        <Col md={6} className="col-md-6 d-flex flex-column p-3">
          <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
            <div className="my-auto py-4">
              <p className="mb-1 opacity-75 text-uppercase">
                LET'S GET YOU STARTED
              </p>
              <h1 className="fw-semibold mb-4 pb-1 fs-2">Create an Account</h1>
              <CommonFormikComponent
                validationSchema={CreateAccountSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {(formikProps) => (
                  <>
                    <CustomInputText
                      wrapperClassname="mb-3 pb-1"
                      controlId="fullName"
                      label="Full Name"
                      touched={formikProps.touched.fullName}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.fullName}
                      value={formikProps.values.fullName}
                      validationSchema={CreateAccountSchema}
                    />

                    <CustomInputText
                      wrapperClassname="mb-3 pb-1"
                      controlId="email"
                      label="Email Address"
                      placeholder="john.smith@example.com"
                      touched={formikProps.touched.email}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.email}
                      value={formikProps.values.email}
                      validationSchema={CreateAccountSchema}
                    />

                    <CustomInputPassword
                      wrapperClassname="mb-3 pb-1"
                      controlId="password"
                      label="Password"
                      touched={formikProps.touched.password}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.password}
                      value={formikProps.values.password}
                      validationSchema={CreateAccountSchema}
                      tooltip={PASSWORD_VALIDATION}
                      tooltipContent={<MdOutlineInfo />}
                    />

                    <CustomInputPassword
                      wrapperClassname="mb-4"
                      controlId="confirmPassword"
                      label="Confirm Password"
                      touched={formikProps.touched.confirmPassword}
                      handleBlur={formikProps.handleBlur}
                      handleChange={formikProps.handleChange}
                      errorsField={formikProps.errors.confirmPassword}
                      value={formikProps.values.confirmPassword}
                      validationSchema={CreateAccountSchema}
                      tooltip={PASSWORD_VALIDATION}
                      tooltipContent={<MdOutlineInfo />}
                    />
                    <Stack
                      direction="horizontal"
                      gap={3}
                      className="justify-content-end"
                    >
                      <Button
                        className="w-100"
                        variant="primary"
                        type="submit"
                        disabled={formikProps.isSubmitting || loading}
                      >
                        {loading ? "Processing..." : "Get Started"}
                      </Button>
                    </Stack>
                  </>
                )}
              </CommonFormikComponent>
            </div>
            <div className="bg-dark bg-opacity-05 custom-min-height-40 fs-13 p-2 rounded text-center w-100 d-inline-flex align-items-center justify-content-center">
              <span className="pe-1">Already have an account? </span>{" "}
              <Link className="fw-semibold text-decoration-none" to="/login">
                Login
              </Link>
            </div>
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
}
